<template>
  <div
    ref="interactiveListTitle"
    :class="bem()"
    style="--progress-in: 1; --progress-out: 0"
  >
    <div v-if="props.title" :class="bem('text-container')">
      <VisionHeading v-if="props.title" size="h1" tag="h2" :class="bem('title')">
        {{ props.title }}
      </VisionHeading>
    </div>
    <MediaItem
      v-if="props.media"
      :item="props.media"
      aspect-ratio="auto"
      :class="bem('media')"
    />
  </div>
</template>

<script setup lang="ts">
const bem = useBEM('InteractiveListTitle')
const { gsap } = useGsap()

interface InteractiveListTitleProps {
  title?: string
  media: {
    mediaType: string
    image?: any
    reel: any[]
    video?: any
    speed?: number
  }
}

const props = defineProps<InteractiveListTitleProps>()

const interactiveListTitle = ref<HTMLElement | null>(null)

onMounted(() => {
  if (interactiveListTitle.value) {
    gsap.set(interactiveListTitle.value, {
      '--progress-in': 0,
    })

    gsap.to(interactiveListTitle.value, {
      scrollTrigger: {
        trigger: interactiveListTitle.value,
        start: 'top bottom',
        end: 'top 25%',
        scrub: 0,
      },
      '--progress-in': 1,
      ease: 'power1.out',
    })

    gsap.to(interactiveListTitle.value, {
      scrollTrigger: {
        trigger: interactiveListTitle.value,
        start: 'top top',
        end: 'bottom top',
        scrub: 0,
      },

      '--progress-out': 1,
    })
  }
})
</script>

<style lang="postcss">
.InteractiveListTitle {
  @apply relative w-full aspect-square;

  @screen lg {
    @apply aspect-auto;
    min-height: 100vh;
    min-height: 100dvh;
    will-change: clip-path;
    clip-path: inset(
      0 calc(50% - calc(var(--progress-in) * 50%)) 0
        calc(50% - calc(var(--progress-in) * 50%)) round
        calc(12px - calc(var(--progress-in) * 12px))
    );
    transform: translate3d(0, calc(var(--progress-out) * 50%), 0);
    transition: clip-path 0.01s ease-in-out;
  }
}

.InteractiveListTitle__media {
  --media-item-border-radius: 0;
  @apply absolute inset-0;

  &::before {
    --bg-opacity: clamp(0, calc(var(--progress-out) / 1.5), 0.8);
    @apply content-[''] inset-0 absolute bg-black bg-opacity-[var(--bg-opacity)] z-[2];
  }

  @screen lg {
    opacity: var(--progress-in);
    transform: scale(calc(1.4 - (0.4 * var(--progress-in))));
    transform-origin: 50% 50%;
  }
}

.InteractiveListTitle__text-container {
  @apply absolute bottom-0 p-[var(--outer-gutter)]
  flex items-end text-white z-[2];

  @screen lg {
    @apply sticky top-full bottom-auto -mb-[100%] -translate-y-full;
  }

  @screen full {
    @apply global-wrapper-large;
    left: calc((100vw - var(--large-screen-size)) / 2);
  }
}

.InteractiveListTitle__title {
  --vision-heading-size: theme('fontSize.2xl');

  @screen md {
    --vision-heading-size: theme('fontSize.4xl');
  }

  @screen lg {
    --vision-heading-size: theme('fontSize.8xl');
  }
}
</style>
